import React, { useState, useRef, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, Loader2, Calendar } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Noise Overlay Component
const NoiseOverlay = () => <div className="noise-overlay" aria-hidden="true" />;

// Pitch Deck Component
const PitchDeck = ({ printMode = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const slideRef = useRef(null);

  // Builds the PDF in-memory. Caller decides whether to save or upload.
  const buildPDF = async () => {
    // Hide navigation elements during PDF generation
    const navElements = document.querySelectorAll('[data-pdf-hide]');
    navElements.forEach(el => el.style.display = 'none');

    try {
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [254, 143],
        compress: true,
      });

      const slideWidth = 254;
      const slideHeight = 143;

      for (let i = 0; i < slides.length; i++) {
        setCurrentSlide(i);
        await new Promise(resolve => setTimeout(resolve, 400));

        const slideElement = document.getElementById('slide-content');
        if (slideElement) {
          const canvas = await html2canvas(slideElement, {
            backgroundColor: '#050505',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: 1920,
            height: 1080,
            windowWidth: 1920,
            windowHeight: 1080,
          });

          const imgData = canvas.toDataURL('image/jpeg', 0.92);

          if (i > 0) {
            pdf.addPage();
          }

          pdf.addImage(imgData, 'JPEG', 0, 0, slideWidth, slideHeight, undefined, 'FAST');

          pdf.setFontSize(8);
          pdf.setTextColor(100, 100, 100);
          pdf.text(`${i + 1} / ${slides.length}`, slideWidth - 15, slideHeight - 5);
        }
      }

      pdf.setProperties({
        title: 'BRUTAL™ Investor Pitch Deck 2026',
        subject: 'Investment Opportunity - The Brand of Vengeance',
        author: 'ARTRYST TECHNOLOGIES PRIVATE LIMITED',
        keywords: 'BRUTAL, investment, pitch deck, lifestyle brand',
        creator: 'BRUTAL™'
      });

      return pdf;
    } finally {
      navElements.forEach(el => el.style.display = '');
      setCurrentSlide(0);
    }
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const pdf = await buildPDF();
      pdf.save('BRUTAL_Investor_Pitch_Deck_2026.pdf');
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed. Please try again or use a desktop browser.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const slides = [
    // Slide 1: Title
    {
      id: 1,
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <div style={{ height: '120px', overflow: 'hidden' }} className="md:!h-[180px] mb-6">
            <img 
              src="/brutal-logo.png" 
              alt="BRUTAL" 
              style={{ height: '170px', objectFit: 'cover', objectPosition: 'top' }}
              className="md:!h-[255px] w-auto mx-auto"
            />
          </div>
          <p className="font-body text-lg md:text-2xl tracking-[0.3em] uppercase text-white/80 mb-8">
            THE BRAND OF VENGEANCE
          </p>
          <div className="border-t border-white/20 pt-8 mt-4">
            <p className="font-body text-sm text-white/50 tracking-wider">INVESTOR PITCH DECK 2026</p>
            <p className="font-body text-xs text-white/30 mt-2">ARTRYST TECHNOLOGIES PRIVATE LIMITED</p>
          </div>
        </div>
      )
    },
    // Slide 2: The Opportunity
    {
      id: 2,
      content: (
        <div className="flex flex-col justify-center h-full px-8 md:px-16">
          <p className="font-body text-xs text-[#FF2A00] tracking-widest uppercase mb-4">01 / THE OPPORTUNITY</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-8">The $350B Lifestyle Market is Ready for Disruption</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border border-white/10 p-6">
              <p className="font-heading text-4xl md:text-5xl font-bold text-[#FF2A00]">$350B</p>
              <p className="font-body text-sm text-white/60 mt-2">Global luxury lifestyle market by 2027</p>
            </div>
            <div className="border border-white/10 p-6">
              <p className="font-heading text-4xl md:text-5xl font-bold text-[#FF2A00]">67%</p>
              <p className="font-body text-sm text-white/60 mt-2">Gen Z & Millennials seeking brand stories</p>
            </div>
            <div className="border border-white/10 p-6">
              <p className="font-heading text-4xl md:text-5xl font-bold text-[#FF2A00]">4.2x</p>
              <p className="font-body text-sm text-white/60 mt-2">Higher engagement with narrative brands</p>
            </div>
          </div>
          <p className="font-body text-sm text-white/50 mt-8 max-w-2xl">
            Consumers don't just buy products—they buy into stories, identities, and movements. BRUTAL delivers all three.
          </p>
        </div>
      )
    },
    // Slide 3: Introducing BRUTAL
    {
      id: 3,
      content: (
        <div className="flex flex-col justify-center h-full px-8 md:px-16">
          <p className="font-body text-xs text-[#FF2A00] tracking-widest uppercase mb-4">02 / THE BRAND</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-6">BRUTAL™</h2>
          <p className="font-body text-xl md:text-2xl text-white/80 mb-8 max-w-3xl">
            A luxury lifestyle brand born from the dark narrative universe of psychological thriller storytelling.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-heading text-lg font-bold uppercase text-[#FF2A00] mb-3">Brand DNA</h3>
              <ul className="font-body text-sm text-white/70 space-y-2">
                <li>• Dark, powerful aesthetic</li>
                <li>• Unapologetic confidence</li>
                <li>• Story-driven identity</li>
                <li>• Premium quality materials</li>
              </ul>
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold uppercase text-[#FF2A00] mb-3">Brand Promise</h3>
              <p className="font-body text-lg text-white/90 font-semibold">
                "Revenge is uncompromised. So will we be."
              </p>
              <p className="font-body text-xs text-white/50 mt-4">
                BRUTAL empowers those who refuse to be ordinary.
              </p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 4: Product Line
    {
      id: 4,
      content: (
        <div className="flex flex-col justify-center h-full px-8 md:px-16">
          <p className="font-body text-xs text-[#FF2A00] tracking-widest uppercase mb-4">03 / PRODUCT LINE</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-8">Premium Lifestyle Products</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: "Apparel", desc: "", price: "$49-$299" },
              { name: "Accessories", desc: "Bags, Jewellery, Perfumes", price: "$49-$199" },
              { name: "Footwear", desc: "", price: "$49-$349" }
            ].map((item, idx) => (
              <div key={idx} className="border border-white/10 p-5 hover:border-[#FF2A00]/50 transition-colors">
                <h3 className="font-heading text-lg font-bold uppercase mb-2">{item.name}</h3>
                {item.desc && <p className="font-body text-xs text-white/60 mb-3">{item.desc}</p>}
                <p className="font-body text-sm text-[#FF2A00]">{item.price}</p>
              </div>
            ))}
          </div>
          <p className="font-body text-sm text-white/50 mt-8">
            All products feature the iconic BRUTAL raven emblem and premium craftsmanship.
          </p>
        </div>
      )
    },
    // Slide 5: BRUTAL DESIGN Integration
    {
      id: 5,
      content: (
        <div className="flex flex-col justify-center h-full px-8 md:px-16">
          <p className="font-body text-xs text-[#FF2A00] tracking-widest uppercase mb-4">04 / MEDIA INTEGRATION</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-6">BRUTAL DESIGN Series</h2>
          <p className="font-body text-lg text-white/80 mb-8 max-w-3xl">
            Our products are featured in the upcoming psychological thriller series—creating organic product placement and story-driven demand.
          </p>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            <div className="border border-[#FF2A00]/30 bg-[#FF2A00]/5 p-6">
              <h3 className="font-heading text-lg font-bold uppercase text-white mb-3">The Series</h3>
              <p className="font-body text-sm text-white/70 mb-4">
                A psychological thriller exploring themes of vengeance, design, and destiny. Characters wear BRUTAL as their armor.
              </p>
              <a href="https://ranjanabaruah.com" target="_blank" rel="noopener noreferrer"
                 className="font-body text-xs text-[#FF2A00] hover:underline">
                ranjanabaruah.com →
              </a>
            </div>
            <div className="border border-white/10 p-6">
              <h3 className="font-heading text-lg font-bold uppercase text-white mb-3">The Synergy</h3>
              <ul className="font-body text-sm text-white/70 space-y-2">
                <li>• Characters wear BRUTAL products</li>
                <li>• Story creates emotional connection</li>
                <li>• Fans become customers</li>
                <li>• Products become collectibles</li>
              </ul>
            </div>
            <div className="relative border border-[#FF2A00]/30 overflow-hidden">
              <img
                src="https://www.ranjanabaruah.com/assets/brutal-poster.jpg"
                alt="BRUTAL DESIGN poster — psychological thriller series, OTT 2027"
                loading="lazy"
                className="w-full h-auto block"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
              <span className="absolute top-2 left-2 font-body text-[9px] tracking-[0.25em] uppercase text-white bg-black/60 backdrop-blur-sm px-2 py-1">
                ● OTT 2027
              </span>
            </div>
          </div>
        </div>
      )
    },
    // Slide 6: Target Market
    {
      id: 6,
      content: (
        <div className="flex flex-col justify-center h-full px-8 md:px-16">
          <p className="font-body text-xs text-[#FF2A00] tracking-widest uppercase mb-4">05 / TARGET MARKET</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-8">Who Wears BRUTAL?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border border-white/10 p-6">
              <p className="font-heading text-2xl font-bold text-[#FF2A00] mb-2">The Ambitious</p>
              <p className="font-body text-sm text-white/60">Age 18-35</p>
              <p className="font-body text-xs text-white/50 mt-3">
                Young professionals who see fashion as armor. They're building empires and need a brand that matches their mindset.
              </p>
            </div>
            <div className="border border-white/10 p-6">
              <p className="font-heading text-2xl font-bold text-[#FF2A00] mb-2">The Rebels</p>
              <p className="font-body text-sm text-white/60">Age 16-28</p>
              <p className="font-body text-xs text-white/50 mt-3">
                Counter-culture enthusiasts, thriller fans, and those who reject mainstream aesthetics. They want to stand out.
              </p>
            </div>
            <div className="border border-white/10 p-6">
              <p className="font-heading text-2xl font-bold text-[#FF2A00] mb-2">The Collectors</p>
              <p className="font-body text-sm text-white/60">Age 25-45</p>
              <p className="font-body text-xs text-white/50 mt-3">
                Story-driven consumers who collect memorabilia. The BRUTAL DESIGN series creates instant collector's value.
              </p>
            </div>
          </div>
          <div className="mt-8 border-t border-white/10 pt-6">
            <p className="font-heading text-lg font-bold">Total Addressable Market: <span className="text-[#FF2A00]">$12B</span></p>
          </div>
        </div>
      )
    },
    // Slide 7: Go-to-Market Strategy
    {
      id: 7,
      content: (
        <div className="flex flex-col justify-center h-full px-8 md:px-16">
          <p className="font-body text-xs text-[#FF2A00] tracking-widest uppercase mb-4">06 / GO-TO-MARKET</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-8">Amazon-First Strategy</h2>
          <div className="flex items-center gap-4 mb-8">
            <svg viewBox="0 0 100 35" className="h-8 w-auto" aria-label="Amazon">
              <text x="2" y="20" fill="#FFFFFF" fontFamily="Arial, sans-serif" fontSize="22" fontWeight="bold" letterSpacing="-0.5">amazon</text>
              <path d="M5 26 Q50 35 78 26" stroke="#FF9900" strokeWidth="3" fill="none" strokeLinecap="round"/>
              <polygon points="78,26 85,24 80,30" fill="#FF9900"/>
            </svg>
            <span className="font-body text-sm text-white/50">Exclusive Launch Partner</span>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-heading text-lg font-bold uppercase text-white mb-4">Why Amazon?</h3>
              <ul className="font-body text-sm text-white/70 space-y-2">
                <li>• 300M+ active customers worldwide</li>
                <li>• Prime delivery infrastructure</li>
                <li>• Built-in trust & reviews system</li>
                <li>• Lower customer acquisition cost</li>
                <li>• Global reach from Day 1</li>
              </ul>
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold uppercase text-white mb-4">Launch Phases</h3>
              <ul className="font-body text-sm text-white/70 space-y-2">
                <li><span className="text-[#FF2A00]">Q4 2027:</span> Core apparel launch</li>
                <li><span className="text-[#FF2A00]">Q2 2028:</span> Accessories expansion</li>
                <li><span className="text-[#FF2A00]">Q4 2028:</span> Footwear collection</li>
                <li><span className="text-[#FF2A00]">2030+:</span> International expansion</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    // Slide 8: Revenue Model
    {
      id: 8,
      content: (
        <div className="flex flex-col justify-center h-full px-8 md:px-16">
          <p className="font-body text-xs text-[#FF2A00] tracking-widest uppercase mb-4">07 / FINANCIALS</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-8">Growth Projections</h2>
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            {[
              { year: "Year 1", revenue: "$50K", growth: "Target" },
              { year: "Year 2", revenue: "$100K", growth: "2x" },
              { year: "Year 3", revenue: "$200K", growth: "2x" },
              { year: "Year 4", revenue: "$400K", growth: "2x" },
              { year: "Year 5", revenue: "$1.2M", growth: "3x" }
            ].map((item, idx) => (
              <div key={idx} className="border border-white/10 p-5 text-center">
                <p className="font-body text-xs text-white/50 mb-2">{item.year}</p>
                <p className="font-heading text-xl md:text-2xl font-bold text-[#FF2A00]">{item.revenue}</p>
                <p className="font-body text-xs text-white/50 mt-2">{item.growth}</p>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-white/10 p-4">
              <p className="font-body text-xs text-white/50 mb-1">Avg. Order Value</p>
              <p className="font-heading text-xl font-bold">$85</p>
            </div>
            <div className="border border-white/10 p-4">
              <p className="font-body text-xs text-white/50 mb-1">Gross Margin Target</p>
              <p className="font-heading text-xl font-bold">Min. 60%</p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 9: Competitive Advantage
    {
      id: 9,
      content: (
        <div className="flex flex-col justify-center h-full px-8 md:px-16">
          <p className="font-body text-xs text-[#FF2A00] tracking-widest uppercase mb-4">08 / MOAT</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-8">Why BRUTAL Wins</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="border-l-2 border-[#FF2A00] pl-4">
                <h3 className="font-heading text-lg font-bold uppercase">Story-Driven Brand</h3>
                <p className="font-body text-sm text-white/60 mt-1">
                  Unlike generic lifestyle brands, BRUTAL has a narrative universe that creates emotional connection and loyalty.
                </p>
              </div>
              <div className="border-l-2 border-[#FF2A00] pl-4">
                <h3 className="font-heading text-lg font-bold uppercase">Media Integration</h3>
                <p className="font-body text-sm text-white/60 mt-1">
                  Products featured in BRUTAL DESIGN series = organic marketing & collector demand.
                </p>
              </div>
              <div className="border-l-2 border-[#FF2A00] pl-4">
                <h3 className="font-heading text-lg font-bold uppercase">Distinct Aesthetic</h3>
                <p className="font-body text-sm text-white/60 mt-1">
                  The raven emblem and dark luxury aesthetic is instantly recognizable and memorable.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="border-l-2 border-white/30 pl-4">
                <h3 className="font-heading text-lg font-bold uppercase">Community First</h3>
                <p className="font-body text-sm text-white/60 mt-1">
                  Building a cult following before mass market—creating demand through scarcity and story.
                </p>
              </div>
              <div className="border-l-2 border-white/30 pl-4">
                <h3 className="font-heading text-lg font-bold uppercase">IP Ownership</h3>
                <p className="font-body text-sm text-white/60 mt-1">
                  Full ownership of brand, story, and characters creates long-term licensing opportunities.
                </p>
              </div>
              <div className="border-l-2 border-white/30 pl-4">
                <h3 className="font-heading text-lg font-bold uppercase">Lean Operations</h3>
                <p className="font-body text-sm text-white/60 mt-1">
                  Amazon fulfillment + on-demand production = minimal inventory risk and capital efficiency.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 10: Call to Action
    {
      id: 10,
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <p className="font-body text-xs text-[#FF2A00] tracking-widest uppercase mb-6">09 / JOIN THE VENGEANCE</p>
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase mb-4">Let's Build This<br/>Empire Together</h2>
          <p className="font-body text-lg text-white/70 mb-8 max-w-xl">
            BRUTAL is more than a brand—it's a movement. We're seeking strategic partners who believe in the power of story-driven commerce.
          </p>
          <a
            href="https://calendly.com/connect-brutallifestyle/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#FF2A00] text-white font-body text-sm font-semibold tracking-[0.2em] uppercase px-8 py-4 mb-8 hover:bg-white hover:text-black transition-colors"
            data-testid="pitch-calendly-cta"
          >
            Book a 30-min Call →
          </a>
          <div className="border border-[#FF2A00] bg-[#FF2A00]/10 p-8 mb-8">
            <p className="font-body text-sm text-white/50 mb-2">CONTACT</p>
            <p className="font-heading text-xl text-white">connect@brutallifestyle.com</p>
          </div>
          <div className="flex items-center gap-8 text-white/50">
            <div className="text-center">
              <p className="font-heading text-2xl font-bold text-white">$50K</p>
              <p className="font-body text-xs">Seed Round</p>
            </div>
            <div className="w-px h-12 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading text-2xl font-bold text-white">Q4 2027</p>
              <p className="font-body text-xs">Launch Date</p>
            </div>
          </div>
          <p className="font-body text-xs text-white/30 mt-12">© 2026 ARTRYST TECHNOLOGIES PRIVATE LIMITED</p>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Print-only mode: renders ALL slides stacked, used by server-side Playwright PDF render.
  if (printMode) {
    return (
      <>
        <style>{`
          @page { size: 1920px 1080px; margin: 0; }
          html, body, #root { background: #050505 !important; margin: 0 !important; padding: 0 !important; }
          .pitch-print-root { width: 1920px; }
          .pitch-print-slide {
            position: relative;
            width: 1920px;
            height: 1080px;
            background: #050505;
            color: #F5F5F5;
            overflow: hidden;
            page-break-after: always;
            break-after: page;
          }
          .pitch-print-slide:last-child { page-break-after: auto; break-after: auto; }
          .pitch-print-slide-inner { width: 100%; height: 100%; }
        `}</style>
        <div className="pitch-print-root" data-pitch-print-ready="true">
          {slides.map((s, idx) => (
            <div className="pitch-print-slide" key={s.id || idx}>
              <div className="pitch-print-slide-inner">{s.content}</div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F5] relative overflow-hidden">
      <NoiseOverlay />
      
      {/* PDF Generation Overlay */}
      {isGeneratingPDF && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex flex-col items-center justify-center">
          <Loader2 size={48} className="animate-spin text-[#FF2A00] mb-4" />
          <p className="font-heading text-xl uppercase">Generating PDF</p>
          <p className="font-body text-sm text-white/50 mt-2">Please wait while we prepare your investor deck...</p>
          <p className="font-body text-xs text-white/30 mt-4">Slide {currentSlide + 1} of {slides.length}</p>
        </div>
      )}
      
      {/* Navigation - Hidden in PDF */}
      <div className="fixed top-3 left-3 right-3 sm:top-4 sm:left-4 sm:right-4 z-50 flex justify-between items-center gap-2" data-pdf-hide>
        <Link to="/" className="font-body text-[10px] sm:text-xs tracking-widest uppercase text-white/50 hover:text-white transition-colors whitespace-nowrap">
          <span className="hidden sm:inline">← Back to Site</span>
          <span className="sm:hidden">← Back</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            className="flex items-center gap-2 font-body text-[10px] sm:text-xs tracking-widest uppercase text-white/50 hover:text-[#FF2A00] transition-colors border border-white/20 hover:border-[#FF2A00] px-2.5 sm:px-3 py-1.5 disabled:opacity-50"
            data-testid="download-pdf"
          >
            {isGeneratingPDF ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span className="hidden sm:inline">Generating PDF...</span>
                <span className="sm:hidden">...</span>
              </>
            ) : (
              <>
                <Download size={14} />
                <span className="hidden sm:inline">Download PDF</span>
                <span className="sm:hidden">PDF</span>
              </>
            )}
          </button>
          <a
            href="https://calendly.com/connect-brutallifestyle/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 font-body text-[10px] sm:text-xs tracking-widest uppercase bg-[#FF2A00] text-white border border-[#FF2A00] px-2.5 sm:px-3 py-1.5 hover:bg-white hover:text-black hover:border-white transition-colors"
            data-testid="toolbar-book-call"
          >
            <Calendar size={14} />
            <span className="hidden sm:inline">Book a Call</span>
            <span className="sm:hidden">Book</span>
          </a>
          <div className="hidden md:block font-body text-xs tracking-widest uppercase text-white/50">
            {currentSlide + 1} / {slides.length}
          </div>
        </div>
      </div>

      {/* Slide Content */}
      <div className="relative z-10 min-h-screen" id="slide-content" ref={slideRef}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: isGeneratingPDF ? 0 : 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isGeneratingPDF ? 0 : -50 }}
            transition={{ duration: isGeneratingPDF ? 0 : 0.3 }}
            className="min-h-screen py-20"
          >
            {slides[currentSlide].content}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Controls - Hidden in PDF */}
      <div className="fixed bottom-8 left-0 right-0 z-50 flex justify-center items-center gap-4" data-pdf-hide>
        <button
          onClick={prevSlide}
          className="w-12 h-12 border border-white/20 flex items-center justify-center hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors"
          data-testid="prev-slide"
        >
          <ChevronLeft size={24} />
        </button>
        
        {/* Slide indicators */}
        <div className="flex gap-2">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-2 h-2 transition-colors ${
                idx === currentSlide ? 'bg-[#FF2A00]' : 'bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
        
        <button
          onClick={nextSlide}
          className="w-12 h-12 border border-white/20 flex items-center justify-center hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors"
          data-testid="next-slide"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Keyboard navigation hint - Hidden in PDF */}
      <div className="fixed bottom-4 right-4 font-body text-xs text-white/30" data-pdf-hide>
        Use ← → keys to navigate
      </div>
    </div>
  );
};

// Main Landing Page
const Home = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [formInView, setFormInView] = useState(true);
  const formRef = useRef(null);
  const emailInputRef = useRef(null);

  useEffect(() => {
    if (!emailInputRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFormInView(entry.isIntersecting),
      { threshold: 0.5, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(emailInputRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => {
    if (!formRef.current) return;
    formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => emailInputRef.current?.focus(), 600);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, contact_email: 'connect@brutallifestyle.com' });
      setStatus('SUBSCRIBED');
      setEmail('');
    } catch (e) {
      setStatus('ERROR');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F5] relative overflow-hidden">
      <NoiseOverlay />
      
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=1920&q=80"
          alt="Background"
          className="w-full h-full object-cover opacity-20 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-[#050505]" />
      </div>
      
      {/* Subtle red glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF2A00]/8 rounded-full blur-[150px]" />
      
      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-16">
        
        {/* Logo Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-6"
        >
          {/* Logo - Original cropped image */}
          <div style={{ height: '130px', overflow: 'hidden' }} className="md:!h-[200px] lg:!h-[240px] mx-auto mb-3">
            <img 
              src="/brutal-logo.png" 
              alt="BRUTAL" 
              style={{ height: '185px', objectFit: 'cover', objectPosition: 'top' }}
              className="md:!h-[285px] lg:!h-[340px] w-auto mx-auto"
              data-testid="hero-logo"
            />
          </div>
          
          {/* New Tagline */}
          <p className="font-body text-xs md:text-base tracking-[0.4em] uppercase text-white/80">
            THE BRAND OF VENGEANCE
          </p>
        </motion.div>

        {/* Launching Soon Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-8"
        >
          <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-black tracking-tight uppercase text-white mb-5">
            Coming Soon
          </h2>
          
          {/* Amazon Badge */}
          <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 px-5 py-2.5">
            <span className="font-body text-xs tracking-widest uppercase text-white/50">Exclusively on</span>
            <svg viewBox="0 0 100 35" className="h-6 md:h-7 w-auto" aria-label="Amazon">
              <text x="2" y="20" fill="#FFFFFF" fontFamily="Arial, sans-serif" fontSize="22" fontWeight="bold" letterSpacing="-0.5">amazon</text>
              <path d="M5 26 Q50 35 78 26" stroke="#FF9900" strokeWidth="3" fill="none" strokeLinecap="round"/>
              <polygon points="78,26 85,24 80,30" fill="#FF9900"/>
            </svg>
          </div>
        </motion.div>

        {/* BRUTAL DESIGN Series Connection */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="text-center max-w-xl mx-auto mb-8 px-4"
        >
          <div className="border border-[#FF2A00]/30 bg-[#FF2A00]/5 p-5">
            <p className="font-body text-xs tracking-widest uppercase text-[#FF2A00] mb-2">
              Our Products Will Be Featured In
            </p>
            <h3 className="font-heading text-lg md:text-xl font-bold uppercase text-white mb-2">
              BRUTAL DESIGN
            </h3>
            <p className="font-body text-[10px] tracking-[0.25em] uppercase text-white/50 mb-3">
              Indian title · RANNJISH
            </p>
            <p className="font-body text-xs text-white/60 mb-3">
              An upcoming psychological thriller series where design meets destiny.<br/>
              The tale of pitiful vengeance.
            </p>
            <a
              href="https://ranjanabaruah.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block font-body text-xs tracking-widest uppercase text-white/70 hover:text-[#FF2A00] transition-colors border-b border-white/30 hover:border-[#FF2A00] pb-1"
              data-testid="brutal-design-link"
            >
              Discover More at ranjanabaruah.com
            </a>
          </div>
        </motion.div>

        {/* RANNJISH Series Logo — clickable, links to /series details */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="max-w-2xl mx-auto mb-10 px-4 text-center"
        >
          <Link
            to="/series"
            className="block group relative border border-[#FF2A00]/30 bg-black overflow-hidden"
            data-testid="rannjish-poster-link"
          >
            <img
              src="/rannjish-logo.png"
              alt="RANNJISH — Indian title of BRUTAL DESIGN"
              loading="lazy"
              className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <span className="absolute top-3 left-3 font-body text-[10px] tracking-[0.3em] uppercase text-white bg-black/60 backdrop-blur-sm px-2 py-1">
              ● Coming OTT 2027
            </span>
          </Link>
          <Link
            to="/series"
            className="inline-flex items-center gap-2 mt-4 font-body text-xs tracking-[0.3em] uppercase text-white/70 hover:text-[#FF2A00] transition-colors border-b border-white/30 hover:border-[#FF2A00] pb-1"
            data-testid="rannjish-details-cta"
          >
            Visit Series Details Here →
          </Link>
        </motion.div>

        {/* Quote Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="text-center max-w-xl mx-auto mb-8 px-4"
        >
          <blockquote className="font-body text-sm italic text-white/50 leading-relaxed">
            "If God is with you, you die by His design...<br />
            and if God is with me,<br />
            <span className="text-[#FF2A00] font-semibold">you die by my design..."</span>
          </blockquote>
        </motion.div>

        {/* Newsletter Signup */}
        <motion.div
          ref={formRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="w-full max-w-md mx-auto"
        >
          <p className="font-body text-xs tracking-widest uppercase text-white/50 text-center mb-4">
            Be the first to experience BRUTAL<sup className="text-[0.6em]">TM</sup>
          </p>
          
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              ref={emailInputRef}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ENTER YOUR EMAIL"
              required
              className="flex-1 bg-transparent border border-white/20 text-white px-4 py-3 font-body text-sm tracking-wider uppercase placeholder:text-white/30 focus:border-[#FF2A00] focus:outline-none transition-colors"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-white text-black px-6 py-3 font-body text-sm font-semibold tracking-wider uppercase hover:bg-[#FF2A00] hover:text-white transition-colors disabled:opacity-50"
              data-testid="newsletter-submit-btn"
            >
              {loading ? 'JOINING...' : 'NOTIFY ME'}
            </button>
          </form>
          
          {status === 'SUBSCRIBED' && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="font-body text-xs text-[#FF2A00] mt-4 tracking-widest uppercase text-center"
            >
              You're in. We'll notify you when BRUTAL<sup className="text-[0.6em]">TM</sup> launches.
            </motion.p>
          )}
          {status === 'ERROR' && (
            <p className="font-body text-xs text-[#FF2A00] mt-4 tracking-widest uppercase text-center">
              Something went wrong. Try again.
            </p>
          )}
        </motion.div>

        {/* Pitch Deck Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-8"
        >
          <Link 
            to="/pitch" 
            className="font-body text-xs tracking-widest uppercase text-white/40 hover:text-[#FF2A00] transition-colors border-b border-white/20 hover:border-[#FF2A00] pb-1"
          >
            View Investor Pitch Deck →
          </Link>
        </motion.div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="w-full text-center px-4 mt-16 sm:mt-20"
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-y-4 sm:gap-y-0 sm:gap-x-6 mb-4">
            <Link
              to="/privacy"
              className="font-body text-xs text-white/40 hover:text-white/70 tracking-wider uppercase transition-colors py-1"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="hidden sm:inline text-white/20">|</span>
            <Link
              to="/terms"
              className="font-body text-xs text-white/40 hover:text-white/70 tracking-wider uppercase transition-colors py-1"
              data-testid="footer-terms-link"
            >
              Terms & Conditions
            </Link>
            <span className="hidden sm:inline text-white/20">|</span>
            <Link
              to="/pitch"
              className="font-body text-xs text-white/40 hover:text-white/70 tracking-wider uppercase transition-colors py-1"
              data-testid="footer-pitch-link"
            >
              Pitch Deck
            </Link>
            <span className="hidden sm:inline text-white/20">|</span>
            <a
              href="mailto:connect@brutallifestyle.com"
              className="font-body text-xs text-white/40 hover:text-white/70 tracking-wider uppercase transition-colors py-1"
              data-testid="footer-contact-link"
            >
              Contact
            </a>
          </div>
          <p className="font-body text-[10px] sm:text-xs text-white/30 tracking-wider uppercase leading-relaxed">
            © 2026 ARTRYST TECHNOLOGIES PRIVATE LIMITED. All Rights Reserved.
          </p>
        </motion.footer>
      </div>

      {/* Sticky Mobile CTA — hidden on desktop, after subscribe, or when form is in view */}
      <AnimatePresence>
        {status !== 'SUBSCRIBED' && !formInView && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="sm:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 bg-gradient-to-t from-[#050505] via-[#050505]/95 to-transparent"
            data-testid="sticky-mobile-cta"
          >
            <button
              onClick={scrollToForm}
              className="w-full bg-[#FF2A00] text-white font-body text-sm font-semibold tracking-[0.2em] uppercase py-4 border border-[#FF2A00] hover:bg-white hover:text-black transition-colors shadow-[0_10px_30px_rgba(255,42,0,0.35)]"
              data-testid="sticky-mobile-cta-btn"
            >
              Notify Me When BRUTAL Launches
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F5] relative">
      <NoiseOverlay />
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16">
        <Link to="/" className="inline-block mb-8 font-body text-xs tracking-widest uppercase text-white/50 hover:text-[#FF2A00] transition-colors">
          ← Back to Home
        </Link>
        
        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase mb-8">Privacy Policy</h1>
        
        <div className="font-body text-sm text-white/70 space-y-6 leading-relaxed">
          <p className="text-white/50 text-xs uppercase tracking-wider">Last Updated: January 2026</p>
          
          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">1. Introduction</h2>
            <p>ARTRYST TECHNOLOGIES PRIVATE LIMITED ("we," "our," or "us") operates the BRUTAL brand and website. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or subscribe to our mailing list.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">2. Information We Collect</h2>
            <p><strong>Personal Information:</strong> When you subscribe to our newsletter or contact us, we may collect your email address and any other information you voluntarily provide.</p>
            <p className="mt-2"><strong>Automatically Collected Information:</strong> We may automatically collect certain information about your device, including your IP address, browser type, operating system, and browsing behavior on our website.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">3. Use of Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-white/60">
              <li>Send you updates about our product launch and brand news</li>
              <li>Respond to your inquiries and communications</li>
              <li>Improve our website and user experience</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">4. Sharing of Information</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share information with service providers who assist us in operating our website and conducting our business, subject to confidentiality agreements.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">5. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">6. Your Rights</h2>
            <p>You have the right to access, correct, or delete your personal information. You may unsubscribe from our mailing list at any time by clicking the unsubscribe link in our emails or contacting us directly.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">7. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <p className="mt-2 text-[#FF2A00]">connect@brutallifestyle.com</p>
          </section>
        </div>

        <footer className="mt-16 pt-8 border-t border-white/10">
          <p className="font-body text-xs text-white/30 tracking-wider uppercase text-center">
            © 2026 ARTRYST TECHNOLOGIES PRIVATE LIMITED. All Rights Reserved.
          </p>
        </footer>
      </div>
    </div>
  );
};

// Terms & Conditions Page
const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F5] relative">
      <NoiseOverlay />
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16">
        <Link to="/" className="inline-block mb-8 font-body text-xs tracking-widest uppercase text-white/50 hover:text-[#FF2A00] transition-colors">
          ← Back to Home
        </Link>
        
        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase mb-8">Terms & Conditions</h1>
        
        <div className="font-body text-sm text-white/70 space-y-6 leading-relaxed">
          <p className="text-white/50 text-xs uppercase tracking-wider">Last Updated: January 2026</p>
          
          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">1. Acceptance of Terms</h2>
            <p>By accessing and using this website, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our website.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">2. Intellectual Property</h2>
            <p>All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of ARTRYST TECHNOLOGIES PRIVATE LIMITED and is protected by intellectual property laws. The BRUTAL brand, logo, and associated trademarks are proprietary to ARTRYST TECHNOLOGIES PRIVATE LIMITED.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">3. Use of Website</h2>
            <p>You agree to use this website only for lawful purposes and in a manner that does not infringe upon the rights of others or restrict their use of the website. Prohibited activities include:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-white/60">
              <li>Attempting to gain unauthorized access to our systems</li>
              <li>Transmitting harmful code or malware</li>
              <li>Using the website for fraudulent purposes</li>
              <li>Reproducing or distributing our content without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">4. Newsletter Subscription</h2>
            <p>By subscribing to our newsletter, you consent to receive marketing communications from us. You may unsubscribe at any time. We reserve the right to modify or discontinue our newsletter service without notice.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">5. Disclaimer</h2>
            <p>This website and its content are provided "as is" without warranties of any kind. We do not guarantee the accuracy, completeness, or timeliness of information on this website. Product images and descriptions are for illustration purposes only.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">6. Limitation of Liability</h2>
            <p>ARTRYST TECHNOLOGIES PRIVATE LIMITED shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of this website or any linked websites.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">7. Governing Law</h2>
            <p>These Terms and Conditions shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in India.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">8. Changes to Terms</h2>
            <p>We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting on this website. Your continued use of the website constitutes acceptance of the modified terms.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase text-white mb-3">9. Contact</h2>
            <p>For questions regarding these Terms and Conditions, please contact:</p>
            <p className="mt-2 text-[#FF2A00]">connect@brutallifestyle.com</p>
          </section>
        </div>

        <footer className="mt-16 pt-8 border-t border-white/10">
          <p className="font-body text-xs text-white/30 tracking-wider uppercase text-center">
            © 2026 ARTRYST TECHNOLOGIES PRIVATE LIMITED. All Rights Reserved.
          </p>
        </footer>
      </div>
    </div>
  );
};

// Series Details page — placeholder for now, user will provide content later
const SeriesDetails = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden">
      {/* Noise overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-5 mix-blend-overlay" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
      }}/>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12 md:py-20">
        <Link
          to="/"
          className="inline-block font-body text-xs tracking-widest uppercase text-white/50 hover:text-white transition-colors mb-10"
          data-testid="series-back-link"
        >
          ← Back to Home
        </Link>

        {/* Hero: RANNJISH logo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-10"
        >
          <div className="relative border border-[#FF2A00]/30 bg-black overflow-hidden">
            <img
              src="/rannjish-logo.png"
              alt="RANNJISH — Indian title of BRUTAL DESIGN"
              className="w-full h-auto block"
            />
            <span className="absolute top-3 left-3 font-body text-[10px] tracking-[0.3em] uppercase text-white bg-black/60 backdrop-blur-sm px-2 py-1">
              ● Coming OTT 2027
            </span>
          </div>
        </motion.div>

        {/* Titles */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="text-center mb-12"
        >
          <p className="font-body text-[10px] tracking-[0.4em] uppercase text-[#FF2A00] mb-3">
            The Series
          </p>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tight mb-3">
            RANNJISH
          </h1>
          <p className="font-body text-sm md:text-base tracking-[0.25em] uppercase text-white/60">
            Global Title · BRUTAL DESIGN
          </p>
        </motion.div>

        {/* Placeholder content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="border border-white/10 bg-white/[0.02] p-8 md:p-12 text-center"
          data-testid="series-details-placeholder"
        >
          <p className="font-body text-[10px] tracking-[0.4em] uppercase text-white/40 mb-4">
            ● Details Loading Soon
          </p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase mb-4">
            Full Series Details Coming Shortly
          </h2>
          <p className="font-body text-sm md:text-base text-white/60 leading-relaxed max-w-xl mx-auto mb-8">
            Synopsis, cast, format, creator notes and the official trailer will be published here soon.
            Meanwhile, catch the vision at the creator&rsquo;s portfolio.
          </p>
          <a
            href="https://ranjanabaruah.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#FF2A00] text-white font-body text-sm font-semibold tracking-[0.2em] uppercase px-6 py-3 hover:bg-white hover:text-black transition-colors"
            data-testid="series-visit-portfolio"
          >
            Visit ranjanabaruah.com →
          </a>
        </motion.div>

        <footer className="mt-16 pt-8 border-t border-white/10 text-center">
          <p className="font-body text-xs text-white/30 tracking-wider uppercase">
            © 2026 ARTRYST TECHNOLOGIES PRIVATE LIMITED
          </p>
        </footer>
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/pitch" element={<PitchDeck />} />
        <Route path="/pitch/print" element={<PitchDeck printMode={true} />} />
        <Route path="/series" element={<SeriesDetails />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
